/* Commonly used facets functions */
import { computed, ref } from 'vue';
import { useScreenOrientation } from '@vueuse/core';
import { useStore } from './useStore';
import useDisplay from './useDisplay';
import config from '../utils/config';

import {
  REPLACE_SELECTED_VALUES,
  REMOVE_CHECKBOX_VALUES_BY_VALUE,
  REMOVE_CUSTOM_CHECKBOX_VALUES,
  ADD_CHECKBOX_VALUES,
  UPDATE_SORTBY_SELECTED,
  ADD_FACET_OPTIONS,
  ADD_SORT_BY_OPTIONS,
  CLEAR_ALL_SELECTED_VALUES,
  SET_DESELECTION_PILLS_ORDER_NAMES,
} from '../store/mutations';

import GET_ALL_SELECTED_CRITERIA from '../store/actions';

const store = ref(null);
const customFacetTypes = ref({});
const isFacetsLoading = ref(false);
const globalOptions = {
  showFacetSelectedValuesList: ref(false),
  maxCustomRangeValueLength: ref(4),
  prioritizedFacets: ref([]),
  drawerOptions: ref({}),
  onSelectedOption: (() => {}),
  slideoutFacets: ref(null),
  maxDesktopFilters: ref(7),
  maxTabletFiltersNumber: ref(5),
  productCount: ref(0),
  totalFacetSelectedCount: ref(0),
  mySizesData: ref({}),
  onSaveMySizesToggle: (() => {}),
  consumerStore: ref(null),
  pageType: ref('registry'),
  onFacetClickedEvent: null,
  facetRowsNumber: ref(8),
  xapiExperimentIds: ref([]),
  deviceInfo: ref({}),
  isSYS: ref(false),
  isDynamicFilters: ref(false),
  isProductsLoading: ref(false),
};

const openedDropdown = ref(null);
const setOpenedDropdown = (dropdown) => {
  openedDropdown.value = dropdown;
};

const slideoutProductCount = computed(() => (
  globalOptions.slideoutFacets.value === null
    ? globalOptions.productCount.value
    : (globalOptions.slideoutFacets.value.productCount || 0)
));

const slideoutFacetSelectedCount = computed(() => (
  globalOptions.slideoutFacets.value === null
    ? globalOptions.totalFacetSelectedCount.value
    : (globalOptions.slideoutFacets.value.totalSelectedFacets || 0)
));

const setLoadingStatus = (val) => { (isFacetsLoading.value = !!val); };

const triggerSelectedFacetsUpdate = () => store.value.dispatch(GET_ALL_SELECTED_CRITERIA);

const replaceSelectedValues = (facet) => {
  store.value.commit(REPLACE_SELECTED_VALUES, { ...facet });
};

const clearAllSelectedValues = () => {
  store.value.commit(CLEAR_ALL_SELECTED_VALUES);
};

const removeCheckboxValuesByValue = (name, value) => {
  store.value.commit(REMOVE_CHECKBOX_VALUES_BY_VALUE, { name, value });
};

const removeCustomCheckboxValues = (name, isCustomOptionFunc) => {
  store.value.commit(REMOVE_CUSTOM_CHECKBOX_VALUES, { name, isCustomOptionFunc });
};

const addCheckboxValues = (name, newValue) => {
  store.value.commit(ADD_CHECKBOX_VALUES, { name, newValue });
};

const updateSortbySelected = (value) => {
  store.value.commit(UPDATE_SORTBY_SELECTED, value);
};

const allSelectedCriteria = computed(() => store.value.getters.allSelectedCriteria);
const allSelectedFacets = computed(() => store.value.getters.allSelectedCriteria.facets);
const facetByName = (name) => store.value.getters.facetByName(name);
const facetByPropery = (options) => store.value.getters.facetByPropery(options);

const addFacetOptions = (facets) => {
  store.value.commit(ADD_FACET_OPTIONS, { facets });
};

const addSortByOptions = (sortBy) => {
  store.value.commit(ADD_SORT_BY_OPTIONS, { sortBy });
};

const setFacetTypes = (newFacetTypes) => {
  customFacetTypes.value = newFacetTypes;
};

const setSlideoutFacets = (val) => {
  globalOptions.slideoutFacets.value = val ? { ...val } : null;
};

const setGlobalOptions = (options) => {
  Object.assign(globalOptions, options);
};

const closeDrawer = () => {
  store.value.commit('slideoutStore/CLOSE', config.sortFilterSlideOutId);
};

const toggleDrawer = () => {
  store.value.commit('slideoutStore/TOGGLE', config.sortFilterSlideOutId);
};

const toggleLocatorDrawer = () => {
  store.value.commit('slideoutStore/TOGGLE', 'locator-slideout-filtering-facets');
};

const toggleZipDrawer = () => {
  store.value.commit('slideoutStore/TOGGLE', 'zipcode-slideout-ff');
};

function prioritizeFacets(facets, facetNames) {
  const splitted = [[], []];
  const sortOrder = facetNames.reduce((order, name, idx) => {
    order[name] = idx;
    return order;
  }, {});
  facets.forEach((facet) => splitted[facetNames.includes(facet.name) ? 0 : 1].push(facet));
  splitted[0].sort((a, b) => sortOrder[a.name] - sortOrder[b.name]);
  return splitted.flat();
}

const facets = computed(() => {
  const prioritizedFacets = globalOptions.prioritizedFacets.value;
  const allFacets = store.value?.getters.facets || [];

  // change the facets order: [...prioritizedFacets, ...the rest]
  const sortedFacets = prioritizedFacets.length ? prioritizeFacets(allFacets, prioritizedFacets) : allFacets;
  return sortedFacets;
});
const sortByOptions = computed(() => store.value.getters.sortBy.sortByOptions);
const sortBy = computed(() => store.value.getters.sortBy);
const sortBySelectedValue = computed(() => store.value.getters.sortBy.selectedValue);

const subscribeFacetsUpdate = (onUpdate) => store.value.subscribeAction({
  after: (action) => {
    /* istanbul ignore else */
    if (action.type === GET_ALL_SELECTED_CRITERIA) {
      onUpdate();
    }
  },
});

// should be called from onMounted() in a component
const subscribeSlideoutToggle = (onUpdate) => (
  globalOptions?.consumerStore?.value?.subscribe((mutation, state) => {
    /* istanbul ignore else */
    if (mutation.type.includes('slideoutStore')) {
      onUpdate(mutation, state);
    }
  })
);

const deselectionPillsNamesOrder = computed(() => store.value.getters.getDeselectionPillsNamesOrder);
const setDeselectionPillsOrderNames = (order) => store.value.commit(SET_DESELECTION_PILLS_ORDER_NAMES, { orderNames: [...order] });

export default function useFacets() {
  store.value = useStore();
  const {
    isMobile,
    isTablet,
    isDesktop,
    displayType,
    isSlideoutMode,
    isDropdownMode,
  } = useDisplay();
  const {
    orientation,
    angle,
  } = useScreenOrientation();

  const isMobileInLandscape = computed(() => isTablet.value && angle.value === 90 && orientation.value?.includes('landscape'));
  const checkboxSpacing = computed(() => ((globalOptions.drawerOptions.value.tabletPhoneMarginIncrement && !isDesktop.value) ? 's' : 'xs'));

  return {
    triggerSelectedFacetsUpdate,
    replaceSelectedValues,
    removeCheckboxValuesByValue,
    removeCustomCheckboxValues,
    addCheckboxValues,
    updateSortbySelected,
    allSelectedCriteria,
    allSelectedFacets,
    subscribeFacetsUpdate,
    addFacetOptions,
    addSortByOptions,
    setFacetTypes,
    setGlobalOptions,
    setSlideoutFacets,
    globalOptions,
    closeDrawer,
    toggleDrawer,
    toggleLocatorDrawer,
    toggleZipDrawer,
    facets,
    facetByName,
    sortByOptions,
    sortBy,
    sortBySelectedValue,
    displayType,
    isMobile,
    isTablet,
    isDesktop,
    isSlideoutMode,
    isDropdownMode,
    customFacetTypes,
    setLoadingStatus,
    isFacetsLoading,
    slideoutProductCount,
    slideoutFacetSelectedCount,
    checkboxSpacing,
    prioritizeFacets,
    subscribeSlideoutToggle,
    clearAllSelectedValues,
    isMobileInLandscape,
    openedDropdown,
    setOpenedDropdown,
    store,
    deselectionPillsNamesOrder,
    setDeselectionPillsOrderNames,
    facetByPropery,
  };
}
